import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { useAuth } from '../../../auth/AuthContext';
import * as api from '../../../api';
import PageHeader from '../../../app/PageHeader';
import useApiRequest from '../../../hooks/useApiRequest';
import Avatar from '../../../components/primitives/Avatar';
import ConfirmPasswordDialog from '../../../components/feedback/ConfirmPasswordDialog';
import StatePanel from '../../../components/feedback/StatePanel';
import ViewAsConfirmationDialog from '../components/ViewAsConfirmationDialog';
import '../AdminUsers.css';

function formFrom(account) {
  return {
    display_name: account.display_name,
    username: account.username,
    email: account.email,
    sponsor_org_id: account.sponsor_org ? String(account.sponsor_org.id) : '',
    is_active: account.is_active,
  };
}

export default function DriverAccountPage() {
  const { user, startImpersonation } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [confirmingDeactivation, setConfirmingDeactivation] = useState(false);
  const [notice, setNotice] = useState('');
  const [viewingAs, setViewingAs] = useState(false);
  const [confirmingViewAs, setConfirmingViewAs] = useState(false);

  const viewAsDriver = async () => {
    setViewingAs(true);
    setErrors({});
    try {
      await startImpersonation(userId);
      navigate('/', { state: { viewAsReturnTo: location.pathname } });
    } catch (error) {
      setErrors({ detail: error.message });
      setConfirmingViewAs(false);
      setViewingAs(false);
    }
  };

  const {
    data,
    setData,
    status,
    reload: load,
  } = useApiRequest(
    useCallback(async () => {
      const [account, organizations] = await Promise.all([
        api.getAdminDriver(userId),
        api.getAdminSponsorOrganizations(),
      ]);
      return { account, organizations };
    }, [userId]),
    {
      skip: user?.account_type !== 'admin',
      onSuccess: ({ account }) => setForm(formFrom(account)),
    },
  );
  const account = data?.account;
  const organizations = data?.organizations || [];

  useEffect(() => {
    document.title = 'Driver account | Good Driver Incentive Program';
  }, []);

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const cancel = () => {
    setForm(formFrom(account));
    setErrors({});
    setEditing(false);
  };

  // Deactivating locks the owner out, so the server asks for the admin's password.
  const save = async (event, currentPassword) => {
    event?.preventDefault();
    const clientErrors = {};
    if (!form.display_name.trim()) clientErrors.display_name = 'Name is required.';
    if (!/^[A-Za-z0-9._-]{3,30}$/.test(form.username))
      clientErrors.username = 'Use 3 to 30 letters, numbers, periods, dashes, or underscores.';
    if (!form.email.trim()) clientErrors.email = 'Email is required.';
    if (Object.keys(clientErrors).length) {
      setErrors(clientErrors);
      return;
    }
    if (account.is_active && !form.is_active && currentPassword === undefined) {
      setConfirmingDeactivation(true);
      return;
    }
    setSaving(true);
    setErrors({});
    try {
      const saved = await api.updateAdminDriver(userId, {
        ...form,
        ...(currentPassword ? { current_password: currentPassword } : {}),
        display_name: form.display_name.trim(),
        username: form.username.trim(),
        email: form.email.trim(),
        sponsor_org_id: form.sponsor_org_id ? Number(form.sponsor_org_id) : null,
      });
      setData((current) => ({ ...current, account: saved }));
      setForm(formFrom(saved));
      setEditing(false);
      setNotice('Driver account saved.');
      setConfirmingDeactivation(false);
    } catch (error) {
      // A wrong password is shown in the password dialog, which stays open.
      if (currentPassword !== undefined && (error.data?.current_password || error.status === 429))
        throw error;
      setConfirmingDeactivation(false);
      setErrors(error.data || { detail: error.message });
    } finally {
      setSaving(false);
    }
  };

  if (user?.account_type !== 'admin' || status === 'forbidden')
    return (
      <main className="sponsor-detail-page">
        <StatePanel
          className="sponsor-detail-state"
          headingLevel={1}
          title="You don't have access to this page"
        >
          <p>Only administrators can view and edit driver accounts.</p>
        </StatePanel>
      </main>
    );
  if (status === 'loading')
    return (
      <main className="sponsor-detail-page">
        <p role="status">Loading driver account…</p>
        <section
          className="card sponsor-detail-card sponsor-detail-skeleton"
          aria-label="Loading the account"
        />
      </main>
    );
  if (status === 'error')
    return (
      <main className="sponsor-detail-page">
        <StatePanel
          className="sponsor-detail-state"
          headingLevel={1}
          tone="error"
          title="This account couldn't be loaded"
        >
          <p>Check your connection and try again.</p>
          <button type="button" onClick={load}>
            Try again
          </button>
        </StatePanel>
      </main>
    );
  if (status === 'not-found')
    return (
      <main className="sponsor-detail-page">
        <StatePanel
          className="sponsor-detail-state"
          headingLevel={1}
          title="That driver account doesn't exist"
        >
          <p>It may have been removed, or the link may be wrong.</p>
          <Link to="/users">Back to users</Link>
        </StatePanel>
      </main>
    );

  return (
    <div className="sponsor-detail-page">
      <PageHeader
        title="Driver account"
        breadcrumb={
          <>
            <Link to="/users">Users</Link> / {account.display_name}
          </>
        }
      />
      <main className="sponsor-detail-content">
        <section className="card sponsor-detail-card">
          <div className="sponsor-detail-card-head">
            <div className="sponsor-detail-person">
              <Avatar
                className="sponsor-detail-avatar"
                name={account.display_name}
                src={account.profile_picture_url}
              />
              <div>
                <h2>{account.display_name}</h2>
                <p>
                  @{account.username} <span>Driver</span> ·{' '}
                  {account.is_active ? 'Active' : 'Inactive'}
                </p>
              </div>
            </div>
            {!editing && (
              <div className="sponsor-detail-header-actions">
                <button
                  type="button"
                  onClick={() => setConfirmingViewAs(true)}
                  disabled={viewingAs || !account.is_active || account.view_as?.eligible === false}
                  aria-describedby={
                    !account.is_active || account.view_as?.eligible === false
                      ? 'driver-view-as-disabled'
                      : undefined
                  }
                >
                  {viewingAs ? 'Opening…' : 'View as driver'}
                </button>
                {(!account.is_active || account.view_as?.eligible === false) && (
                  <p className="view-as-disabled-reason" id="driver-view-as-disabled">
                    {!account.is_active
                      ? 'Inactive accounts cannot be viewed as.'
                      : account.view_as.detail}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setNotice('');
                    setEditing(true);
                  }}
                >
                  Edit account
                </button>
              </div>
            )}
          </div>
          {errors.detail && !editing && (
            <p className="banner banner-error sponsor-detail-banner" role="alert">
              {errors.detail}
            </p>
          )}
          {notice && (
            <p className="banner banner-success sponsor-detail-banner" role="status">
              {notice}
            </p>
          )}
          {!editing ? (
            <dl className="sponsor-detail-view">
              <div>
                <dt>Full name</dt>
                <dd>{account.display_name}</dd>
              </div>
              <div>
                <dt>Username</dt>
                <dd>@{account.username}</dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd>{account.email}</dd>
              </div>
              <div>
                <dt>Sponsor organization</dt>
                <dd>{account.sponsor_org?.name || <i>Not assigned</i>}</dd>
              </div>
              <div>
                <dt>Account status</dt>
                <dd>{account.is_active ? 'Active' : 'Inactive'}</dd>
              </div>
              <div>
                <dt>Account type</dt>
                <dd>
                  Driver <small>Locked after account creation</small>
                </dd>
              </div>
              <div>
                <dt>Profile photo</dt>
                <dd>
                  {account.profile_picture_url ? 'Uploaded' : <i>None uploaded</i>}{' '}
                  <small>Only the account holder can change this</small>
                </dd>
              </div>
            </dl>
          ) : (
            <form onSubmit={save} noValidate>
              {errors.detail && (
                <p className="banner banner-error sponsor-detail-banner" role="alert">
                  {errors.detail}
                </p>
              )}
              <div className="sponsor-detail-form">
                <label>
                  Full name
                  <input
                    value={form.display_name}
                    onChange={(e) => update('display_name', e.target.value)}
                  />
                  {errors.display_name && <small>{errors.display_name}</small>}
                </label>
                <label>
                  Username
                  <input
                    value={form.username}
                    onChange={(e) => update('username', e.target.value)}
                  />
                  {errors.username && <small>{errors.username}</small>}
                </label>
                <label>
                  Email
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                  />
                  {errors.email && <small>{errors.email}</small>}
                </label>
                <label>
                  Sponsor organization
                  <select
                    value={form.sponsor_org_id}
                    onChange={(e) => update('sponsor_org_id', e.target.value)}
                  >
                    <option value="">Not assigned</option>
                    {organizations.map((org) => (
                      <option value={org.id} key={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                  <small>Optional. Leave unassigned until the driver joins a sponsor.</small>
                </label>
                <label>
                  Account status
                  <select
                    value={String(form.is_active)}
                    onChange={(e) => update('is_active', e.target.value === 'true')}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                  <small>An inactive account cannot sign in.</small>
                </label>
                <div className="sponsor-detail-locked">
                  <span>Account type</span>
                  <strong>Driver</strong>
                  <small>Cannot be changed after account creation.</small>
                </div>
              </div>
              <div className="sponsor-detail-actions">
                <button type="button" onClick={cancel} disabled={saving}>
                  Cancel
                </button>
                <button className="primary" type="submit" disabled={saving}>
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          )}
        </section>
      </main>
      {confirmingDeactivation && (
        <ConfirmPasswordDialog
          title="Deactivate this account?"
          message={`${account.display_name} won't be able to sign in until the account is made active again. Enter your password to confirm.`}
          confirmLabel="Deactivate account"
          onConfirm={(password) => save(null, password)}
          onCancel={() => setConfirmingDeactivation(false)}
        />
      )}
      {confirmingViewAs && (
        <ViewAsConfirmationDialog
          account={{
            name: account.display_name,
            username: account.username,
            role: 'driver',
            organization: account.sponsor_org?.name,
            avatarSrc: account.profile_picture_url,
            isActive: account.is_active,
          }}
          administratorName={user.name || user.username || 'your administrator account'}
          busy={viewingAs}
          onConfirm={viewAsDriver}
          onCancel={() => setConfirmingViewAs(false)}
        />
      )}
    </div>
  );
}
